import { useState } from "react";
import { Link } from "react-router-dom";
import news from "../config/news";
import { Blog } from "../models/blog";
import NewsModal from "./news/NewsModal";

function getCurrentEvent(): Blog | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Find upcoming events (sortDate >= today), sorted ascending so nearest is first
  const upcoming = news
    .filter((item) => {
      if (!item.sortDate) return false;
      const eventDate = new Date(item.sortDate);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    })
    .sort((a, b) => (a.sortDate ?? "").localeCompare(b.sortDate ?? ""));

  return upcoming.length > 0 ? upcoming[0] : null;
}

export default function CurrentEvent() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const event = getCurrentEvent();

  if (!event) return null;

  return (
    <>
      <section className="relative bg-white pt-12 lg:pt-24 pb-8 lg:pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section label */}
          <div
            className="text-center mb-6 lg:mb-10 aos-init"
            data-aos="fade-down"
            data-aos-duration={600}
          >
            <span className="inline-block bg-red-700 text-white text-xs sm:text-sm font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
              Aktuelle Veranstaltung
            </span>
          </div>

          {/* Event card */}
          <div
            className="relative rounded-2xl overflow-hidden shadow-xl cursor-pointer group aos-init"
            data-aos="fade-up"
            data-aos-duration={600}
            data-aos-delay={100}
            onClick={() => setIsModalOpen(true)}
          >
            {/* Background image */}
            <div className="relative h-72 sm:h-96 lg:h-[28rem]">
              <img
                src={event.image}
                alt={event.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Gradient overlay — left-to-right so only the text area is darkened */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />

              {/* Content over the image */}
              <div className="absolute inset-y-0 left-0 flex flex-col justify-end p-6 sm:p-8 lg:p-12 text-white w-2/3 sm:w-1/2 lg:w-5/12">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold leading-tight mb-3">
                  {event.title}
                </h2>

                {event.date && (
                  <p className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {event.date}
                  </p>
                )}

                <p className="text-sm sm:text-base lg:text-lg text-gray-200 line-clamp-3">
                  {event.excerpt}
                </p>

                <div className="mt-4 sm:mt-6">
                  <span className="inline-flex items-center px-5 py-2.5 bg-red-700 hover:bg-red-600 text-white font-bold text-sm sm:text-base rounded-2xl transition-colors duration-300">
                    Mehr erfahren
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 ml-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsModal
        news={event}
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
      />
    </>
  );
}
